/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ============================================
// Shop by Color / Color Family Recommendation
// ============================================
(function () {
  'use strict';

  // --- Helper Functions ---

  function getSelectedColor() {
    var checkedInput = document.querySelector('.product-form input[name="option1"]:checked, .product-form select[name="option1"]');
    if (!checkedInput) return null;
    var value = checkedInput.value || '';
    var parts = value.split('_ql_');
    return parts.length >= 2 ? parts[parts.length - 1] : value;
  }

  function getSelectedProductId() {
    var checkedInput = document.querySelector('.product-form input[name="option1"]:checked, .product-form select[name="option1"]');
    if (!checkedInput) return null;
    var value = checkedInput.value || '';
    var parts = value.split('_ql_');
    return parts.length >= 2 ? parts[0] : value;
  }

  function parseColorSwatchConfig(config) {
    var result = {};
    var lines;
    if (Array.isArray(config)) {
      lines = config;
    } else if (typeof config === 'string') {
      lines = config.split('\n');
    } else {
      lines = [];
    }
    lines.forEach(function (line) {
      var colonIndex = line.indexOf(':');
      if (colonIndex === -1) return;
      var name = line.substring(0, colonIndex).trim().toLowerCase();
      var value = line.substring(colonIndex + 1).trim();
      if (name) result[name] = value;
    });
    return result;
  }

  function getColorSwatchStyle(colorName, config) {
    var parsed = parseColorSwatchConfig(config);
    var colorLower = (colorName || '').toLowerCase().trim();
    var value = parsed[colorLower];
    if (value) {
      if (value.charAt(0) === '#') {
        return 'background-color: ' + value;
      } else {
        return 'background: var(--' + value + ')';
      }
    }
    var cssVar = colorLower.replace(/\s+/g, '-');
    return 'background: var(--' + cssVar + ', #cccccc)';
  }

  function findMatchingVariant(product, colorName) {
    if (!product || !product.variants) return null;
    var colorLower = (colorName || '').toLowerCase();
    var i, variant;
    // Try option1, option2, option3 in order
    for (let optIdx = 0; optIdx < product.options.length; optIdx++) {
      for (i = 0; i < product.variants.length; i++) {
        variant = product.variants[i];
        if (product.options[optIdx].values[variant.options[optIdx]].toLowerCase() === colorLower && 
            variant.available > 0) {
          return variant;
        }
      }
    }
    // Fallback: any available variant with matching option1 (case-insensitive)
    for (let optIdx = 0; optIdx < product.options.length; optIdx++) {
      for (i = 0; i < product.variants.length; i++) {
        variant = product.variants[i];
        if (product.options[optIdx].values[variant.options[optIdx]].toLowerCase() === colorLower) {
          return variant;
        }
      }
    }
    return null;
  }

  function formatPrice(cents) {
    var settings = window.themeVariables && window.themeVariables.settings;
    if (!settings) return '$' + (cents / 100).toFixed(2);
    var format = settings.currencyCodeEnabled
      ? settings.moneyWithCurrencyFormat
      : settings.moneyFormat;
    if (!format) return '$' + (cents / 100).toFixed(2);

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    if (!match) return '$' + (cents / 100).toFixed(2);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    var match = url.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (match) {
      var prefix = url.split(match[0]);
      var suffix = match[0];
      return (prefix[0] + '_' + size + suffix).replace(/http(s)?:/, '');
    }
    return url;
  }

  function getProductImage(product, variant) {
    if (variant && variant.imageIndex != null && product.images && product.images.length > variant.imageIndex) {
      return product.images[variant.imageIndex].url;
    }else if(product.images && product.images.length > 0) return product.images[0].url;
    return '';
  }

  function getImageAspectRatio(product, variant) {
    var img = null;
    if (variant && variant.imageIndex != null && product.images) {
      if(product.images.length > variant.imageIndex) img = product.images[variant.imageIndex];
      else if(product.images.length > 0) img = product.images[0];
    } else if (product.images && product.images.length > 0) {
      img = product.images[0];
    }
    if (img && img.width && img.height && img.height > 0) {
      return img.width / img.height;
    }
    return 0.8; // Default 4:5
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // --- ShopByColorCarousel Custom Element ---

  class ShopByColorCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.productListInner = this.querySelector('.product-list__inner');
      this.prevNextButtons = this.querySelector('prev-next-buttons');
      this.productItems = [];
      this._scrollTimer = null;

      this.addEventListener('prev-next:prev', this._onPrev.bind(this));
      this.addEventListener('prev-next:next', this._onNext.bind(this));

      if (this.productListInner) {
        var self = this;
        this.productListInner.addEventListener('scroll', function () {
          clearTimeout(self._scrollTimer);
          self._scrollTimer = setTimeout(function () {
            self._updateButtonStates();
          }, 50);
        });
      }

      this._updateButtonStates();
    }

    updateItems() {
      this.productItems = Array.from(this.querySelectorAll('product-item'));
      if (this.productListInner) {
        this.productListInner.scrollLeft = 0;
      }
      this._updateButtonStates();
    }

    _getItemWidth() {
      if (this.productItems.length === 0) return 0;
      return this.productItems[0].getBoundingClientRect().width;
    }

    _getColumnGap() {
      var gap = parseInt(getComputedStyle(this).getPropertyValue('--product-list-column-gap'));
      return isNaN(gap) ? 24 : gap;
    }

    _onPrev() {
      if (this.productItems.length === 0) return;
      var scrollAmount = this._getItemWidth() + this._getColumnGap();
      this.productListInner.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }

    _onNext() {
      if (this.productItems.length === 0) return;
      var scrollAmount = this._getItemWidth() + this._getColumnGap();
      this.productListInner.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    _updateButtonStates() {
      if (!this.prevNextButtons) return;
      var prevBtn = this.prevNextButtons.querySelector('.prev-next-button--prev');
      var nextBtn = this.prevNextButtons.querySelector('.prev-next-button--next');
      if (!this.productListInner) return;

      var scrollLeft = this.productListInner.scrollLeft;
      var maxScroll = this.productListInner.scrollWidth - this.productListInner.clientWidth;

      if (prevBtn) prevBtn.disabled = scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  // --- ShopByColor Custom Element ---

  class ShopByColor extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      var self = this;
      this._cache = {};
      this._drawerIds = [];
      this._requestId = 0;
      this._checkTimer = null;

      this.blockType = this.getAttribute('data-type') || '';

      if (this.blockType === 'shop_by_color_family') {
        this.colorFamilies = JSON.parse(this.getAttribute('data-color-families') || '[]')
          .map(function (line) {
            return line.split('|').map(function (c) { return c.trim().toLowerCase(); }).filter(function (c) { return c.length > 0; });
          })
          .filter(function (group) { return group.length >= 2; });
        this.colorList = [];
      } else {
        this.colorFamilies = [];
        this.colorList = JSON.parse(this.getAttribute('data-color-list') || '[]')
          .map(function (c) { return c.trim().toLowerCase(); })
          .filter(function (c) { return c.length > 0; });
      }

      this.carousel = this.querySelector('shop-by-color-carousel');
      this.productListInner = this.querySelector('.product-list__inner');
      this.prevNextButtons = this.querySelector('prev-next-buttons');

      // Listen for color changes
      this._onDocumentChange = function (event) {
        var target = event.target;
        if (target && target.matches && target.matches('[name="option1"]')) {
          // 忽略 quick-buy 弹窗内的颜色切换，避免重新渲染时关闭当前弹窗
          if (target.closest && target.closest('quick-buy-drawer, quick-buy-popover')) return;
          clearTimeout(self._checkTimer);
          self._checkTimer = setTimeout(function () { self._checkColor(); }, 100);
        }
      };
      this._onVariantChanged = function (event) {
        // 忽略 quick-buy 弹窗内的变体变化
        if (event.target && event.target.closest && event.target.closest('quick-buy-drawer, quick-buy-popover')) return;
        clearTimeout(self._checkTimer);
        self._checkTimer = setTimeout(function () { self._checkColor(); }, 100);
      };

      document.addEventListener('change', this._onDocumentChange);
      document.addEventListener('variant:changed', this._onVariantChanged);

      // Check initial color after a short delay
      setTimeout(function () { self._checkColor(); }, 300);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onDocumentChange);
      document.removeEventListener('variant:changed', this._onVariantChanged);
      this._cleanupDrawers();
    }

    _checkColor() {
      var colorName = getSelectedColor();
      if (!colorName) return;

      var colorLower = colorName.toLowerCase();

      if (this.blockType === 'shop_by_color_family') {
        var matchedFamily = null;
        for (var i = 0; i < this.colorFamilies.length; i++) {
          if (this.colorFamilies[i].indexOf(colorLower) !== -1) {
            matchedFamily = this.colorFamilies[i];
            break;
          }
        }
        if (matchedFamily) {
          var otherColors = matchedFamily.filter(function (c) { return c !== colorLower; });
          this.removeAttribute('hidden');
          this._fetchFamilyProducts(otherColors, colorName);
        } else {
          this._hideBlock();
        }
      } else {
        if (this.colorList.indexOf(colorLower) !== -1) {
          this.removeAttribute('hidden');
          this._fetchProducts(colorName);
        } else {
          this._hideBlock();
        }
      }
    }

    _hideBlock() {
      this.setAttribute('hidden', '');
    //   this.closest('section').setAttribute('hidden', '');
      this._cleanupDrawers();
    }

    _showLoading() {
      if (this.productListInner) {
        this.productListInner.innerHTML = '<div class="shop-by-color__loading"><div class="shop-by-color__loading-spinner"></div></div>';
      }
      if (this.prevNextButtons) {
        this.prevNextButtons.style.display = 'none';
      }
    }

    _fetchProducts(colorName) {
      var self = this;

      // Check cache
      if (this._cache[colorName]) {
        this._renderProducts(this._cache[colorName], colorName);
        return;
      }

      this._showLoading();
      this._requestId++;
      var currentRequestId = this._requestId;

      var handle = colorName.toLowerCase().replace(/\s+/g, '-');
      /* 改成向usf插件服务器请求数据，直接请求shopify系列页产品数据，多了会识别为可疑流量并拦截了(需要人机验证) */
      // var url = '/collections/shop-by-color-' + handle + '/products.json?limit=15';
      var url = `${usf.settings.searchSvcUrl}search?q=&apiKey=${usf.settings.siteId}&getProductDescription=0&collection=shop-by-color-${handle}&showFacets=1&skip=0&take=15`;

      console.log('ShopByColor: fetching URL:', url); 

      fetch(url)
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (currentRequestId !== self._requestId) return; // Stale response
          var products = (data && data.data.items) || [];
          self._cache[colorName] = products;
          self._renderProducts(products, colorName);
        })
        .catch(function (error) {
          if (currentRequestId !== self._requestId) return;
          console.error('ShopByColor: Failed to fetch products for', colorName, error);
          if (self.productListInner) {
            self.productListInner.innerHTML = '<div class="shop-by-color__empty">Failed to load products</div>';
          }
          if (self.prevNextButtons) {
            self.prevNextButtons.style.display = 'none';
          }
        });
    }

    _fetchFamilyProducts(otherColors, selectedColor) {
      var self = this;
      var cacheKey = 'family::' + selectedColor.toLowerCase();
      if (this._cache[cacheKey]) {
        this._renderFamilyProducts(this._cache[cacheKey]);
        return;
      }
        
      this._showLoading();
      this._requestId++;
      var currentRequestId = this._requestId;

      var TOTAL_PRODUCTS = 8;
      var otherCount = otherColors.length;
      var perColorQuota = Math.floor(TOTAL_PRODUCTS / otherCount);
      var quotas = [];
      for (var q = 0; q < otherCount; q++) {
        quotas.push(q < otherCount - 1 ? perColorQuota : TOTAL_PRODUCTS - perColorQuota * (otherCount - 1));
      }

      var perColorResults = new Array(otherCount);
      var completed = 0;

      otherColors.forEach(function (color, index) {
        var handle = color.replace(/\s+/g, '-');
        /* 改成向usf插件服务器请求数据，直接请求shopify系列页产品数据，多了会识别为可疑流量并拦截了(需要人机验证) */
      // var url = '/collections/shop-by-color-' + handle + '/products.json?limit=15';
      var url = `${usf.settings.searchSvcUrl}search?q=&apiKey=${usf.settings.siteId}&getProductDescription=0&collection=shop-by-color-${handle}&showFacets=1&skip=0&take=15`;


        fetch(url)
          .then(function (response) { return response.json(); })
          .then(function (data) {
            perColorResults[index] = (data && data.data.items) || [];
          })
          .catch(function (error) {
            console.error('ShopByColor: Failed to fetch products for', color, error);
            perColorResults[index] = [];
          })
          .then(function () {
            completed++;
            if (completed === otherCount) {
              if (currentRequestId !== self._requestId) return;

              var finalProducts = [];
              var seenIds = {};

              perColorResults.forEach(function (products, colorIndex) {
                var quota = quotas[colorIndex];
                var count = 0;
                for (var i = 0; i < products.length && count < quota; i++) {
                  var product = products[i];
                  if (product.id == getSelectedProductId()) continue;
                  if (seenIds[product.id]) continue;
                  seenIds[product.id] = true;
                  finalProducts.push({ product: product, matchedColor: otherColors[colorIndex] });
                  count++;
                }
              });

              self._cache[cacheKey] = finalProducts;
              self._renderFamilyProducts(finalProducts);
            }
          });
      });
    }

    _renderFamilyProducts(products) {
      var self = this;

      this._cleanupDrawers();

      if (this.productListInner) {
        this.productListInner.innerHTML = '';
      }

      var validProducts = 0;
      var smallestAspectRatio = 0;

      products.forEach(function (item, index) {
        var product = item.product;
        var colorName = item.matchedColor;
        var matchedVariant = findMatchingVariant(product, colorName);

        if (!matchedVariant) return;

        validProducts++;
        var aspectRatio = getImageAspectRatio(product, matchedVariant);
        if (aspectRatio > 0) {
          smallestAspectRatio = smallestAspectRatio === 0 ? aspectRatio : Math.min(smallestAspectRatio, aspectRatio);
        }

        var cardHtml = self._createProductCard(product, matchedVariant, colorName, index);
        self.productListInner.insertAdjacentHTML('beforeend', cardHtml);
      });

      if (validProducts === 0) {
        this._hideBlock();
        return;
      }

      if (this.prevNextButtons) {
        if (validProducts > 4) {
          this.prevNextButtons.style.display = '';
          if (smallestAspectRatio > 0) {
            this.prevNextButtons.style.setProperty('--smallest-image-aspect-ratio', smallestAspectRatio);
          } else {
            this.prevNextButtons.style.setProperty('--smallest-image-aspect-ratio', 0.8);
          }
        } else {
          this.prevNextButtons.style.display = 'none';
        }
      }

      if (this.carousel) {
        this.carousel.updateItems();
      }
    }

    _renderProducts(products, colorName) {
      var self = this;

      // Cleanup old drawers
      this._cleanupDrawers();

      // Clear product list
      if (this.productListInner) {
        this.productListInner.innerHTML = '';
      }
      var TOTAL_PRODUCTS = 8;
      var validProducts = 0;
      var smallestAspectRatio = 0;

      products.forEach(function (product, index) {
        // 超过8个产品就不要
        if (validProducts >= TOTAL_PRODUCTS) return;
        // 当前产品本身也不要
        if (product.id == getSelectedProductId()) return;

        var variant = findMatchingVariant(product, colorName);
        if (!variant) return;

        validProducts++;
        var aspectRatio = getImageAspectRatio(product, variant);
        if (aspectRatio > 0) {
          smallestAspectRatio = smallestAspectRatio === 0 ? aspectRatio : Math.min(smallestAspectRatio, aspectRatio);
        }

        var cardHtml = self._createProductCard(product, variant, colorName, index);
        self.productListInner.insertAdjacentHTML('beforeend', cardHtml);
      });

      if (validProducts === 0) {
        this._hideBlock();
        return;
      }

      // Show/hide prev-next buttons
      if (this.prevNextButtons) {
        if (validProducts > 4) {
          this.prevNextButtons.style.display = '';
          if (smallestAspectRatio > 0) {
            this.prevNextButtons.style.setProperty('--smallest-image-aspect-ratio', smallestAspectRatio);
          } else {
            this.prevNextButtons.style.setProperty('--smallest-image-aspect-ratio', 0.8);
          }
        } else {
          this.prevNextButtons.style.display = 'none';
        }
      }

      // Update carousel items
      if (this.carousel) {
        this.carousel.updateItems();
      }
    }

    _createProductCard(product, variant, colorName, index) {
      ShopByColor._idCounter++;
      var uniqueId = 'sbc-' + ShopByColor._idCounter;
      var imageUrl = getProductImage(product, variant);
      var sizedImageUrl = imageUrl ? getSizedImageUrl(imageUrl, '400x') : '';
      var formattedPrice = formatPrice(variant.price * 100);
      var aspectRatio = getImageAspectRatio(product, variant);
      var paddingBottom = aspectRatio > 0 ? (100 / aspectRatio).toFixed(2) : '125';
      var productHandle = product.urlName || product.handle;
      var productUrl = '/products/' + productHandle + '?variant=' + variant.id;

      // Create drawer and popover elements
      var drawerHtml =
        '<quick-buy-drawer id="' + uniqueId + '-drawer" href="' + productUrl + '&view=quick-buy-drawer" class="drawer drawer--large drawer--quick-buy hidden-phone"></quick-buy-drawer>' +
        '<quick-buy-popover id="' + uniqueId + '-popover" href="' + productUrl + '&view=quick-buy-popover" class="popover popover--quick-buy hidden-tablet-and-up"></quick-buy-popover>';
    //   document.body.insertAdjacentHTML('beforeend', drawerHtml);
      this._drawerIds.push(uniqueId + '-drawer', uniqueId + '-popover');

      var html =
        '<product-item class="product-item" data-handle="' + escapeHtml(productHandle) + '">' +
          '<div class="product-item__image-wrapper">' +
            '<a href="' + productUrl + '" data-instant class="product-item__aspect-ratio aspect-ratio" style="padding-bottom: ' + paddingBottom + '%;--aspect-ratio: ' + aspectRatio + ';">' +
              (sizedImageUrl ? '<img src="' + sizedImageUrl + '" loading="lazy" alt="' + escapeHtml(product.title) + '" />' : '') +
            '</a>' +
          '</div>' +
          '<div class="product-item__info">' +
            '<div class="product-item-meta">' +
              '<div class="product-item-meta__title-row">' +
                '<a href="' + productUrl + '" data-instant class="product-item-meta__title">' + escapeHtml(product.title) + '</a>' +
                '<span class="price">' + formattedPrice + '</span>' +
              '</div>' +
              '<div>' +
              '<p class="product-item-meta__color">Color <span style="color: #000000;">' + escapeHtml(colorName) + '</span></p>' +
              '<button is="toggle-button" loader aria-controls="' + uniqueId + '-drawer" aria-expanded="false" class="button button--text shop-by-color__quick-view-button hidden-phone">Quick View</button>' +
              '<button is="toggle-button" aria-controls="' + uniqueId + '-popover" aria-expanded="false" class="button button--text shop-by-color__quick-view-button hidden-tablet-and-up">Quick View</button>' +
            drawerHtml +
              '</div>' +
            '</div>' +
          '</div>' +
        '</product-item>';

      return html;
    }

    _cleanupDrawers() {
      this._drawerIds.forEach(function (id) {
        var el = document.getElementById(id);
        if (el) {
          try {
            if (el.open) el.open = false;
          } catch (e) {}
          el.remove();
        }
      });
      this._drawerIds = [];
    }
  }

  ShopByColor._idCounter = 0;

  // --- Similar Products Toggle (handle切换时显示/隐藏对应分组) ---
  (function () {
    var block = document.getElementById('ql-similar-products-block');
    var productList = document.getElementById('ql-similar-product-list');
    if (!block || !productList) return;

    // 从 main-product 预渲染的 template 中获取同类产品 HTML
    var tpl = document.getElementById('ql-similar-products-tpl');
    if (tpl && tpl.content) {
      productList.appendChild(tpl.content.cloneNode(true));
    }

    var groups = productList.querySelectorAll('.ql-similar-group');
    if (!groups.length) return;

    function updateVisibility(handle) {
      var visibleCount = 0;
      groups.forEach(function (group) {
        if (group.getAttribute('data-handle') === handle) {
          group.removeAttribute('hidden');
          visibleCount++;
        } else {
          group.setAttribute('hidden', '');
        }
      });
      if (visibleCount === 0) {
        block.setAttribute('hidden', '');
      } else {
        block.removeAttribute('hidden');
      }
    }

    // 初始显示当前货号的同类产品
    var currentHandle = block.getAttribute('data-current-handle') || '';
    if (currentHandle) updateVisibility(currentHandle);

    document.addEventListener('handle:changed', function (e) {
      updateVisibility(e.detail.handle);
    });
  })();

  // Register custom elements (carousel first so it upgrades before parent)
  window.customElements.define('shop-by-color-carousel', ShopByColorCarousel);
  window.customElements.define('shop-by-color', ShopByColor);
})();